import { Button, MultiSelect, Select, Stack, Text } from '@mantine/core';
import { useEffect } from 'react';
import {
  useCallableContext,
  useCompositionImplementations,
} from '@/hooks/api/automations/useExecutionCompositions.ts';
import type {
  ExactTrigger,
  ExactVersion,
  CallableContext,
  Location,
  PrepareRequest,
} from '@/types/executionCompositions.ts';
import type { LogicalSubAutomationStep } from '@/types/automationSteps.ts';
import {
  locationKey,
  matchingInheritedCall,
  type InheritedCall,
} from './compositionPresentation.ts';
import { ExactDetails, ImplementationLabel, LocationLabel } from './CompositionPresentation.tsx';

export function ImplementationSelect({
  automationId,
  value,
  onChange,
  disabled = false,
}: {
  automationId: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}) {
  const drafts = useCompositionImplementations(automationId, false);
  const published = useCompositionImplementations(automationId, true);
  const verified =
    drafts.isFetchedAfterMount &&
    published.isFetchedAfterMount &&
    !drafts.isError &&
    !published.isError;
  const versions = verified
    ? [
        ...(drafts.data?.pages.flatMap((page) => page.results) ?? []),
        ...(published.data?.pages.flatMap((page) => page.results) ?? []),
      ]
    : [];
  const unique = [...new Map(versions.map((version) => [version.id, version])).values()];
  return (
    <Stack gap="xs">
      <Select
        label={`Implementation for ${automationId}`}
        searchable
        disabled={disabled || !verified || drafts.isError || published.isError}
        value={value}
        onChange={onChange}
        data={[
          ...(value && !unique.some((version) => version.id === value)
            ? [{ value, label: `Selected exact implementation · ${value}` }]
            : []),
          ...unique.map((version) => ({
            value: version.id,
            label: [
              `v${version.version_number ?? '?'}`,
              version.name?.trim(),
              version.published_at ? 'historically published' : null,
              version.id,
            ].filter(Boolean).join(' · '),
          })),
        ]}
      />
      {value && <ExactDetails values={{ 'Selected version ID': value }} />}
      {(drafts.isError || published.isError) && (
        <Stack gap="xs">
          <Text c="red">Implementation discovery unavailable. Refresh before selecting.</Text>
          <Button
            variant="subtle"
            disabled={disabled || drafts.isFetching || published.isFetching}
            onClick={() => {
              if (drafts.isError)
                void (drafts.isFetchNextPageError ? drafts.fetchNextPage() : drafts.refetch());
              if (published.isError)
                void (published.isFetchNextPageError
                  ? published.fetchNextPage()
                  : published.refetch());
            }}
          >
            Retry implementation discovery
          </Button>
        </Stack>
      )}
      {(drafts.hasNextPage || published.hasNextPage) && (
        <Button
          variant="subtle"
          disabled={disabled || !verified || drafts.isFetching || published.isFetching}
          onClick={() => {
            if (drafts.hasNextPage) void drafts.fetchNextPage();
            if (published.hasNextPage) void published.fetchNextPage();
          }}
        >
          Load more implementations
        </Button>
      )}
    </Stack>
  );
}

/** Inspect selected immutable source; no automatic implementation choices. */
export function InitialCalls({
  sourceVersionId,
  root,
  chain = [],
  selections,
  onSelection,
  disabled,
  inherited = new Map(),
  replacements = [],
}: {
  sourceVersionId: string;
  root: ExactTrigger;
  chain?: Location['call_chain'];
  selections: PrepareRequest['initial_selections'];
  onSelection: (
    location: Location,
    automationId: string,
    versionId: string | null,
    previous?: ExactVersion,
  ) => void;
  disabled: boolean;
  inherited?: Map<string, InheritedCall>;
  replacements?: PrepareRequest['selections'];
}) {
  const source = useCallableContext(sourceVersionId);
  if (chain.length >= 64)
    return (
      <Text c="red">
        Nested selection limit reached. Server compilation must resolve this structure.
      </Text>
    );
  if (source.isError)
    return (
      <Stack gap="xs">
        <Text c="red">Call discovery unavailable. Candidate cannot be completed.</Text>
        <Button
          variant="subtle"
          disabled={disabled || source.isFetching}
          onClick={() => void source.refetch()}
        >
          Retry call discovery
        </Button>
      </Stack>
    );
  if (!source.data || !source.isFetchedAfterMount)
    return <Text>Loading exact call occurrences…</Text>;
  return (
    <Stack>
      {source.data.calls
        .filter(
          (call): call is CallableContext['calls'][number] & { step: LogicalSubAutomationStep } =>
            call.step.binding_mode === 'composition',
        )
        .map((call) => {
          const location = {
            root: {
              automation_id: root.automation_id,
              automation_version_id: root.automation_version_id,
              trigger_step_id: root.trigger_step_id,
            },
            call_chain: [...chain, call.path],
          };
          const key = locationKey(location);
          const previous = matchingInheritedCall(inherited.get(key), call.step);
          const prior = previous?.automation_version_id;
          const chosen =
            replacements.find((item) => locationKey(item.location) === key)?.replacement
              .automation_version_id ??
            selections.find((item) => locationKey(item.location) === key)?.implementation
              .automation_version_id ??
            prior ??
            null;
          return (
            <Stack key={key} gap="xs">
              <Text size="sm">
                <LocationLabel location={location} />
              </Text>
              {prior && (
                <Text size="sm">
                  Inherited selection:{' '}
                  <ImplementationLabel
                    version={{
                      automation_id: call.step.target_automation_id,
                      automation_version_id: prior,
                    }}
                  />
                </Text>
              )}
              <ImplementationSelect
                automationId={call.step.target_automation_id}
                value={chosen}
                disabled={disabled}
                onChange={(versionId) =>
                  onSelection(location, call.step.target_automation_id, versionId, previous)
                }
              />
              {chosen && (
                <InitialCalls
                  sourceVersionId={chosen}
                  root={root}
                  chain={location.call_chain}
                  selections={selections}
                  onSelection={onSelection}
                  disabled={disabled}
                  inherited={inherited}
                  replacements={replacements}
                />
              )}
            </Stack>
          );
        })}
    </Stack>
  );
}

export function RootTriggers({
  versionId,
  role,
  onChange,
  selected,
  disabled,
}: {
  versionId: string;
  role: 'entrypoint' | 'supporting_member';
  onChange: (roots: PrepareRequest['roots']) => void;
  selected: PrepareRequest['roots'];
  disabled: boolean;
}) {
  const source = useCallableContext(versionId);
  // A changed exact source requires an explicit new trigger selection.
  useEffect(() => {
    if (selected.some((root) => root.automation_version_id !== versionId)) onChange([]);
  }, [versionId, selected, onChange]);
  if (source.isError)
    return (
      <Stack gap="xs">
        <Text c="red">Callable source unavailable.</Text>
        <Button
          variant="subtle"
          disabled={disabled || source.isFetching}
          onClick={() => void source.refetch()}
        >
          Retry callable source
        </Button>
      </Stack>
    );
  if (!source.data || !source.isFetchedAfterMount) return <Text>Loading triggers…</Text>;
  return (
    <MultiSelect
      label="Execution triggers"
      disabled={disabled}
      value={selected.map((root) => root.trigger_step_id)}
      data={source.data.triggers.map((trigger) => ({
        value: trigger.step_id,
        label: `${trigger.name} · ${trigger.step_id}`,
      }))}
      onChange={(values) =>
        onChange(
          values.map((value) => ({
            automation_id: source.data!.automation_id,
            automation_version_id: versionId,
            trigger_step_id: value,
            role,
          })),
        )
      }
    />
  );
}
